import { useCallback, useEffect, useRef } from "react";
import { isToolUIPart, type ChatStatus } from "ai";
import {
  isDebugTraceDataPart,
  redactDebugPayload,
  type DebugTraceEvent
} from "@/shared/types/debug-trace";
import type { ChatUIMessage } from "@/shared/types/chat-ui-message";

interface UseDebugMessageIngestionOptions {
  status: ChatStatus;
  messages: ChatUIMessage[];
  enabled: boolean;
  emitClientDebugEvent: (event: Partial<DebugTraceEvent>) => void;
}

function summarizeMessagePart(part: unknown): Record<string, unknown> {
  if (!part || typeof part !== "object") {
    return { kind: typeof part };
  }

  const candidate = part as {
    type?: string;
    text?: string;
    state?: string;
    output?: unknown;
    errorText?: string;
    data?: unknown;
    url?: string;
  };

  if (candidate.type === "text" || candidate.type === "reasoning") {
    return {
      type: candidate.type,
      textLength: candidate.text?.length || 0
    };
  }

  if (candidate.type === "source-url") {
    return {
      type: "source-url",
      url: candidate.url
    };
  }

  if (candidate.type?.startsWith("data-")) {
    return {
      type: candidate.type,
      hasData: candidate.data != null
    };
  }

  if (candidate.type?.startsWith("tool-") || isToolUIPart(part as never)) {
    return {
      type: candidate.type,
      state: candidate.state,
      hasOutput: candidate.output != null,
      hasError: Boolean(candidate.errorText)
    };
  }

  return {
    type: candidate.type || "unknown"
  };
}

function hasRenderableAssistantPart(part: unknown): boolean {
  if (!part || typeof part !== "object") {
    return false;
  }

  const candidate = part as { type?: unknown; text?: unknown };
  if (typeof candidate.type !== "string") {
    return false;
  }

  if (candidate.type === "text") {
    return typeof candidate.text === "string" && candidate.text.trim().length > 0;
  }

  if (candidate.type === "reasoning") {
    return typeof candidate.text === "string" && candidate.text.trim().length > 0;
  }

  if (candidate.type === "source-url") {
    return true;
  }

  if (candidate.type === "dynamic-tool" || candidate.type.startsWith("tool-")) {
    return true;
  }

  return (
    candidate.type === "data-entityCards" ||
    candidate.type === "data-entitySummary" ||
    candidate.type === "data-entityNotice"
  );
}

function summarizeAssistantMessageForError(message: ChatUIMessage | undefined) {
  if (!message) {
    return { present: false };
  }

  const parts = message.parts || [];
  return {
    present: true,
    messageId: message.id,
    partCount: parts.length,
    hasRenderablePart: parts.some((part) => hasRenderableAssistantPart(part)),
    parts: parts.map((part) => summarizeMessagePart(part))
  };
}

export function useDebugMessageIngestion({
  status,
  messages,
  enabled,
  emitClientDebugEvent
}: UseDebugMessageIngestionOptions) {
  const processedDebugEventIdsRef = useRef(new Set<string>());
  const processedMessageIdsRef = useRef(new Set<string>());
  const processedToolPartKeysRef = useRef(new Set<string>());
  const previousStatusRef = useRef(status);

  const resetProcessedMessageTracking = useCallback(() => {
    processedDebugEventIdsRef.current.clear();
    processedMessageIdsRef.current.clear();
    processedToolPartKeysRef.current.clear();
    previousStatusRef.current = "ready";
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (previousStatusRef.current !== status) {
      emitClientDebugEvent({
        category: "stream",
        level: "info",
        title: "Chat status changed",
        payload: {
          previous: previousStatusRef.current,
          next: status
        }
      });

      if (status === "error") {
        const latestAssistantMessage = messages
          .slice()
          .reverse()
          .find((message) => message.role === "assistant");

        emitClientDebugEvent({
          category: "error",
          level: "error",
          title: "Chat entered error state",
          payload: {
            latestAssistantMessage: summarizeAssistantMessageForError(
              latestAssistantMessage
            )
          }
        });
      }

      previousStatusRef.current = status;
    }
  }, [enabled, messages, status, emitClientDebugEvent]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    for (const message of messages) {
      if (processedMessageIdsRef.current.has(message.id)) {
        continue;
      }

      processedMessageIdsRef.current.add(message.id);
      emitClientDebugEvent({
        category: "stream",
        level: "info",
        title: `Message received (${message.role})`,
        messageId: message.id,
        payload: {
          role: message.role,
          partCount: message.parts?.length || 0,
          parts: (message.parts || []).map((part) => summarizeMessagePart(part))
        }
      });

      if (
        message.role === "assistant" &&
        (message.parts?.length ?? 0) === 0
      ) {
        emitClientDebugEvent({
          category: "error",
          level: "warn",
          title: "Assistant message received without parts",
          messageId: message.id,
          payload: {
            role: message.role,
            partCount: 0
          }
        });
      } else if (
        message.role === "assistant" &&
        !(message.parts || []).some((part) => hasRenderableAssistantPart(part))
      ) {
        emitClientDebugEvent({
          category: "error",
          level: "warn",
          title: "Assistant message has no renderable parts",
          messageId: message.id,
          payload: {
            role: message.role,
            partCount: message.parts?.length || 0,
            parts: (message.parts || []).map((part) => summarizeMessagePart(part))
          }
        });
      }
    }

    for (const message of messages) {
      for (const part of message.parts || []) {
        if (!isToolUIPart(part)) {
          continue;
        }

        const toolKey = `${message.id}:${part.toolCallId}:${part.state}`;
        if (processedToolPartKeysRef.current.has(toolKey)) {
          continue;
        }

        processedToolPartKeysRef.current.add(toolKey);
        emitClientDebugEvent({
          source: "client",
          category: "tool",
          level: part.errorText ? "error" : "info",
          title: `Tool part ${part.state}`,
          turnId: `turn-${message.id}`,
          messageId: message.id,
          toolName: part.type.replace("tool-", ""),
          payload: {
            toolCallId: part.toolCallId,
            state: part.state,
            input: part.input,
            output: part.output,
            error: part.errorText
          }
        });
      }
    }

    for (const message of messages) {
      for (const part of message.parts || []) {
        if (!isDebugTraceDataPart(part)) {
          continue;
        }

        if (processedDebugEventIdsRef.current.has(part.data.id)) {
          continue;
        }

        processedDebugEventIdsRef.current.add(part.data.id);

        emitClientDebugEvent({
          ...part.data,
          source: "server",
          payload: redactDebugPayload(part.data.payload)
        });
      }
    }
  }, [messages, enabled, emitClientDebugEvent]);

  return {
    resetProcessedMessageTracking
  };
}
